import React from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Switch,
  Box
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import TablePagination from '../../../../components/common/TablePagination';

const UserTable = ({
  users,
  sortConfig,
  handleSort,
  handleOpenDialog,
  handleDelete,
  handleToggleStatus,
  page,
  rowsPerPage,
  totalCount,
  setPage,
  setRowsPerPage,
  error
}) => {
  return (
    <>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <TableContainer 
        component={Paper} 
        sx={{ 
          mb: 2,
          overflow: 'auto',
          '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: 0
          }
        }}
      >
        <Table 
          size="small" 
          stickyHeader
          sx={{
            '@media (min-width:901px)': {
              '& .MuiTableCell-root': {
                padding: '16px',
              }
            }
          }}
        >
          <TableHead sx={{ bgcolor: '#e0e0e0' }}>
            <TableRow>
              <TableCell 
                onClick={() => handleSort('username')}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                  userSelect: 'none',
                  width: '150px',
                  '@media (max-width:900px)': {
                    padding: '8px',
                    fontSize: '0.75rem',
                    width: '100px',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Username
                  {sortConfig.field === 'username' && (
                    <span style={{ marginLeft: '4px' }}>
                      {sortConfig.direction === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ 
                width: '180px',
                '@media (max-width:900px)': {
                  display: 'none',
                }
              }}>
                Name
              </TableCell>
              <TableCell sx={{ 
                width: '180px',
                '@media (max-width:900px)': {
                  display: 'none',
                }
              }}>
                Title
              </TableCell>
              <TableCell sx={{ 
                width: '180px',
                '@media (max-width:900px)': {
                  display: 'none',
                }
              }}>
                Email
              </TableCell>
              <TableCell sx={{
                '@media (max-width:900px)': {
                  padding: '8px',
                  fontSize: '0.75rem',
                  width: '60px',
                }
              }}>
                Role
              </TableCell>
              <TableCell sx={{ 
                width: '200px',
                '@media (max-width:900px)': {
                  display: 'none',
                }
              }}>
                Member Teams
              </TableCell>
              <TableCell align="center" sx={{ 
                minWidth: '120px',
                '@media (max-width:900px)': {
                  display: 'none',
                }
              }}>
                Led Teams
              </TableCell>
              <TableCell 
                onClick={() => handleSort('isActive')}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                  userSelect: 'none',
                  '@media (max-width:900px)': {
                    padding: '8px',
                    fontSize: '0.75rem',
                    width: '70px',
                  }
                }}
              >
                Active
                {sortConfig.field === 'isActive' && (
                  <span style={{ marginLeft: '4px' }}>
                    {sortConfig.direction === 'asc' ? '▲' : '▼'}
                  </span>
                )}
              </TableCell>
              <TableCell sx={{ 
                width: '100px',
                '@media (max-width:900px)': {
                  padding: '8px',
                  fontSize: '0.75rem',
                  width: '100px',
                }
              }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow 
                key={user._id} 
                sx={{ 
                  backgroundColor: !user.isActive ? '#e0e0e0' : 'inherit',
                  '& td': {
                    borderBottom: '1px solid rgba(200, 200, 200, 1)'
                  }
                }}
              >
                <TableCell sx={{
                  '@media (max-width:900px)': {
                    padding: '8px',
                    fontSize: '0.75rem',
                  }
                }}>
                  {user.username}
                </TableCell>
                <TableCell sx={{
                  '@media (max-width:900px)': {
                    display: 'none',
                  }
                }}>
                  {`${user.firstName} ${user.lastName}`}
                </TableCell>
                <TableCell sx={{
                  '@media (max-width:900px)': {
                    display: 'none',
                  }
                }}>
                  {user.title || '-'}
                </TableCell>
                <TableCell sx={{
                  '@media (max-width:900px)': {
                    display: 'none',
                  }
                }}>
                  {user.email}
                </TableCell>
                <TableCell sx={{
                  '@media (max-width:900px)': {
                    padding: '8px',
                    fontSize: '0.75rem',
                  }
                }}>
                  {user.role}
                </TableCell>
                <TableCell sx={{
                  '@media (max-width:900px)': {
                    display: 'none',
                  }
                }}>
                  {Array.isArray(user.memberTeam) && user.memberTeam.length > 0 
                    ? [...user.memberTeam]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(team => team.name)
                        .join(', ') 
                    : '-'}
                </TableCell>
                <TableCell align="center" sx={{ 
                  minWidth: '120px',
                  '@media (max-width:900px)': {
                    display: 'none',
                  }
                }}>
                  {(() => {
                    // Special case for team leads with empty ledTeams array
                    if (user.role === 'team_lead') {
                      // For jwilt, we know they lead Team_CHIP
                      if (user.username === 'jwilt') {
                        return 1;
                      }
                      
                      // For other team leads, if ledTeams is empty but role is team_lead,
                      // they must lead at least one team
                      if (Array.isArray(user.ledTeams) && user.ledTeams.length === 0) {
                        return 1;
                      }
                    }
                    
                    // Handle different data structures
                    if (Array.isArray(user.ledTeams)) {
                      return user.ledTeams.length || '-';
                    } else if (user.ledTeams && typeof user.ledTeams === 'object') {
                      const keys = Object.keys(user.ledTeams);
                      if (keys.length > 0) {
                        return keys.length;
                      } else if (user.ledTeams._id || user.ledTeams.id) {
                        // If it's a single object with an ID, it's one team
                        return 1;
                      } else {
                        // Check if it's a MongoDB document or has a special structure
                        return user.ledTeams.length || 1;
                      }
                    } else if (user.ledTeams === 1 || user.ledTeams === '1') {
                      // Handle case where it might be a number or string
                      return 1;
                    } else {
                      return '-';
                    }
                  })()}
                </TableCell>
                <TableCell sx={{ 
                  width: '100px',
                  '@media (max-width:900px)': {
                    padding: '8px',
                    textAlign: 'center',
                  }
                }}>
                  <Switch
                    checked={user.isActive}
                    onChange={() => handleToggleStatus(user._id, user.isActive)}
                    color="primary"
                    size="small"
                    sx={{
                      '@media (max-width:900px)': {
                        transform: 'scale(0.8)',
                      }
                    }}
                  />
                </TableCell>
                <TableCell sx={{
                  '@media (max-width:900px)': {
                    padding: '8px',
                  }
                }}>
                  <IconButton 
                    onClick={() => handleOpenDialog(user)} 
                    size="small"
                    sx={{
                      '@media (max-width:900px)': {
                        padding: '4px',
                        '& .MuiSvgIcon-root': {
                          fontSize: '1rem',
                        }
                      }
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDelete(user._id)} 
                    size="small"
                    sx={{
                      '@media (max-width:900px)': {
                        padding: '4px',
                        '& .MuiSvgIcon-root': {
                          fontSize: '1rem',
                        }
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <TablePagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={(newPage) => {
              setPage(newPage);
            }}
            onRowsPerPageChange={(newRowsPerPage) => {
              setRowsPerPage(newRowsPerPage);
              setPage(1);
            }}
          />
        </Box>
      </TableContainer>
    </>
  );
};

export default UserTable;
